import React, { useEffect, useState } from 'react';
import { Debt, DebtDetails } from '../../types';
import {
  decreaseInstallmentAmount,
  fullDebtPayment,
  getDebtById,
  increaseDebtInstallments,
  increaseInstallmentAmount
} from '../../services/api/debtsApi';
import { formatDateToEMEA } from '../../utils/dateFormatter';
import { useTranslation } from '../../hooks/useTranslation';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import './PaymentDetailsModal.css';
import { isDebtFullyPaid } from '../../utils/isDebtFullyPaid';

interface PaymentDetailsModalProps {
  debt: Debt;
  onClose: () => void;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({ debt, onClose }) => {
  const { t } = useTranslation();
  const { userType } = useSelector((state: RootState) => state.auth);
  const [debtDetails, setDebtDetails] = useState<DebtDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [installmentAmountDrafts, setInstallmentAmountDrafts] = useState<Record<string, string>>({});
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [fullPaymentAmount, setFullPaymentAmount] = useState<string>('');
  const [fullPaymentDate, setFullPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [newTotalInstallmentsDraft, setNewTotalInstallmentsDraft] = useState<string>('');

  useEffect(() => {
    let isMounted = true;
    
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        setActionError(null);
        setActionSuccess(null);
        setInstallmentAmountDrafts({});
        setActionLoadingKey(null);
        setFullPaymentDate(new Date().toISOString().slice(0, 10));
        setFullPaymentAmount(String((debt.remainingBalance ?? debt.remainingAmount ?? 0) || debt.totalDebt || ''));
        setDebtDetails(null);
        const details = await getDebtById(debt.id.toString());
        if (isMounted) {
          if (details) {
            setDebtDetails(details);
            const currentTotal = details.numberOfInstallments || 0;
            setNewTotalInstallmentsDraft(String(currentTotal + 1));
          } else {
            setError(t('paymentDetails.failedToLoad'));
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching debt details:', err);
        if (isMounted) {
          setError(t('paymentDetails.failedToLoad'));
          setLoading(false);
        }
      }
    };

    fetchDetails();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debt.id]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MRU' // Adjust currency as needed
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'PAID':
        return 'paid';
      case 'PENDING':
        return 'pending';
      case 'OVERDUE':
        return 'overdue';
      default:
        return '';
    }
  };

  const getStatusTranslation = (status: string): string => {
    switch (status) {
      case 'PAID':
        return t('status.paid');
      case 'PENDING':
        return t('status.pending');
      case 'OVERDUE':
        return t('status.overdue');
      default:
        return status;
    }
  };

  const canManageInstallments = userType === 'TWIZE_ADMIN' || userType === 'PARTNER_MANAGER';

  const parsePositiveAmount = (value: string): number | null => {
    const normalized = value.replace(',', '.').trim();
    const num = Number(normalized);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  };

  const parsePositiveInteger = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    if (!Number.isInteger(num) || num < 1) return null;
    return num;
  };

  const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const maybeMessage =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return maybeMessage || fallback;
  };

  const refreshDebtDetails = async () => {
    const details = await getDebtById(debt.id.toString());
    setDebtDetails(details);
    const currentTotal = details?.numberOfInstallments || 0;
    setNewTotalInstallmentsDraft(String(currentTotal + 1));
  };

  const getInstallmentKey = (installment: any): string => {
    return installment.id || String(installment.installmentNumber);
  };

  const handleInstallmentUpdate = async (installment: any, direction: 'increase' | 'decrease') => {
    const installmentId = installment.id as string | undefined;
    const key = getInstallmentKey(installment);
    const draft = installmentAmountDrafts[key] ?? '';
    const amount = parsePositiveAmount(draft);

    setActionError(null);
    setActionSuccess(null);

    if (!installmentId) {
      setActionError(t('paymentDetails.actions.installmentIdMissing'));
      return;
    }
    if (!amount) {
      setActionError(t('paymentDetails.actions.invalidAmount'));
      return;
    }

    try {
      setActionLoadingKey(`${direction}:${key}`);
      if (direction === 'increase') {
        await increaseInstallmentAmount(installmentId, amount);
        setActionSuccess(t('paymentDetails.actions.increaseSuccess'));
      } else {
        await decreaseInstallmentAmount(installmentId, amount);
        setActionSuccess(t('paymentDetails.actions.decreaseSuccess'));
      }
      await refreshDebtDetails();
      setInstallmentAmountDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      console.error('Installment update failed:', e);
      setActionError(getApiErrorMessage(e, t('paymentDetails.actions.updateFailed')));
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleFullPayment = async () => {
    setActionError(null);
    setActionSuccess(null);
    const amount = parsePositiveAmount(fullPaymentAmount);
    if (!amount) {
      setActionError(t('paymentDetails.actions.invalidAmount'));
      return;
    }
    if (!fullPaymentDate) {
      setActionError(t('paymentDetails.actions.invalidDate'));
      return;
    }

    try {
      setActionLoadingKey('fullPayment');
      await fullDebtPayment(debt.id.toString(), amount, fullPaymentDate);
      setActionSuccess(t('paymentDetails.actions.fullPaymentSuccess'));
      await refreshDebtDetails();
    } catch (e) {
      console.error('Full payment failed:', e);
      setActionError(getApiErrorMessage(e, t('paymentDetails.actions.fullPaymentFailed')));
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleIncreaseDebtInstallments = async () => {
    setActionError(null);
    setActionSuccess(null);

    const newTotalInstallments = parsePositiveInteger(newTotalInstallmentsDraft);
    if (!newTotalInstallments) {
      setActionError(t('paymentDetails.actions.invalidInstallmentsCount'));
      return;
    }
    const currentTotalInstallments = debtDetails?.numberOfInstallments ?? debt.numberOfInstallments ?? 0;
    if (newTotalInstallments <= currentTotalInstallments) {
      setActionError(
        t('paymentDetails.actions.installmentsMustBeGreaterThanCurrent', {
          current: currentTotalInstallments
        })
      );
      return;
    }

    try {
      setActionLoadingKey('increaseInstallments');
      await increaseDebtInstallments(debt.id.toString(), newTotalInstallments);
      setActionSuccess(t('paymentDetails.actions.increaseInstallmentsSuccess'));
      await refreshDebtDetails();
    } catch (e) {
      console.error('Increase debt installments failed:', e);
      setActionError(getApiErrorMessage(e, t('paymentDetails.actions.increaseInstallmentsFailed')));
    } finally {
      setActionLoadingKey(null);
    }
  };

  const currentTotalInstallments = debtDetails?.numberOfInstallments ?? debt.numberOfInstallments ?? 0;
  const minimumAllowedInstallments = currentTotalInstallments + 1;
  const parsedNewTotalInstallments = parsePositiveInteger(newTotalInstallmentsDraft);
  const canSubmitIncreaseInstallments =
    parsedNewTotalInstallments !== null && parsedNewTotalInstallments >= minimumAllowedInstallments;

  const getShortId = (id: string): string => {
    // Get the last segment of the UUID
    return id.split('-').pop() || id.substring(id.length - 8);
  };

  const debtFullyPaid = debtDetails ? isDebtFullyPaid(debt, debtDetails) : false;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('paymentDetails.title', { id: getShortId(debt.id) })}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">{t('paymentDetails.loading')}</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : debtDetails ? (
            <>
              <div className="installments-header">
                <div className="header-item">
                  <span className="header-label">{t('paymentDetails.totalDebt')}:</span>
                  <span className="header-value">{formatCurrency(debtDetails.totalDebt)}</span>
                </div>
                <div className="header-item">
                  <span className="header-label">{t('paymentDetails.totalInstallments')}:</span>
                  <span className="header-value">{debtDetails.numberOfInstallments}</span>
                </div>
                <div className="header-item">
                  <span className="header-label">{t('paymentDetails.firstPaymentDate')}:</span>
                  <span className="header-value">{formatDateToEMEA(debtDetails.firstPaymentDate)}</span>
                </div>
              </div>

              {canManageInstallments && !debtFullyPaid && (
                <div className="installments-actions">
                  <div className="full-payment">
                    <div className="full-payment-title">{t('paymentDetails.actions.fullPaymentTitle')}</div>
                    <div className="full-payment-remaining">
                      {t('paymentDetails.actions.remainingToPay')}:&nbsp;
                      <strong>
                        {formatCurrency(debt.remainingBalance ?? debt.remainingAmount ?? debt.totalDebt)}
                      </strong>
                    </div>
                    <div className="full-payment-form">
                      <input
                        className="amount-input"
                        type="text"
                        readOnly
                        value={fullPaymentAmount}
                      />
                      <input
                        className="date-input"
                        type="date"
                        value={fullPaymentDate}
                        onChange={(e) => setFullPaymentDate(e.target.value)}
                      />
                      <button
                        className="primary-action-button"
                        onClick={handleFullPayment}
                        disabled={actionLoadingKey === 'fullPayment'}
                      >
                        {actionLoadingKey === 'fullPayment'
                          ? t('paymentDetails.actions.processing')
                          : t('paymentDetails.actions.payInFull')}
                      </button>
                    </div>
                  </div>

                  <div className="full-payment">
                    <div className="full-payment-title">{t('paymentDetails.actions.increaseInstallmentsTitle')}</div>
                    <div className="full-payment-form">
                      <input
                        className="amount-input"
                        type="number"
                        min={minimumAllowedInstallments}
                        step="1"
                        value={newTotalInstallmentsDraft}
                        onChange={(e) => setNewTotalInstallmentsDraft(e.target.value)}
                        placeholder={t('paymentDetails.actions.installmentsCountPlaceholder')}
                      />
                      <button
                        className="primary-action-button"
                        onClick={handleIncreaseDebtInstallments}
                        disabled={actionLoadingKey === 'increaseInstallments' || !canSubmitIncreaseInstallments}
                      >
                        {actionLoadingKey === 'increaseInstallments'
                          ? t('paymentDetails.actions.processing')
                          : t('paymentDetails.actions.increaseInstallments')}
                      </button>
                    </div>
                  </div>

                  {actionError && <div className="action-error">{actionError}</div>}
                  {actionSuccess && <div className="action-success">{actionSuccess}</div>}
                </div>
              )}

              <div className="table-container">
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>{t('paymentDetails.table.number')}</th>
                      <th>{t('paymentDetails.table.amount')}</th>
                      <th>{t('paymentDetails.table.scheduledDate')}</th>
                      <th>{t('paymentDetails.table.status')}</th>
                      {canManageInstallments && <th>{t('paymentDetails.table.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {debtDetails.installments.length === 0 ? (
                      <tr>
                        <td colSpan={canManageInstallments ? 5 : 4} className="no-data">{t('paymentDetails.table.noInstallments')}</td>
                      </tr>
                    ) : (() => {
                      const sorted = [...debtDetails.installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
                      const nextEditable = sorted.find((inst) => inst.status !== 'PAID' && !inst.paid && !inst.isPaid);
                      const nextEditableKey = nextEditable ? getInstallmentKey(nextEditable) : null;

                      return sorted.map((installment) => {
                        const key = getInstallmentKey(installment);
                        const isEditable = canManageInstallments && nextEditableKey === key;

                        return (
                          <tr key={key}>
                            <td>{installment.installmentNumber}</td>
                            <td>{formatCurrency(installment.amount)}</td>
                            <td>{formatDateToEMEA(installment.scheduledPaymentDate)}</td>
                            <td>
                              <span className={`status-badge ${getStatusBadgeClass(installment.status)}`}>
                                {getStatusTranslation(installment.status)}
                              </span>
                            </td>
                            {canManageInstallments && (
                              <td>
                                {isEditable && (
                                  <div className="installment-action-cell">
                                    <input
                                      className="amount-input"
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={installmentAmountDrafts[getInstallmentKey(installment)] ?? ''}
                                      onChange={(e) => {
                                        const k = getInstallmentKey(installment);
                                        setInstallmentAmountDrafts((prev) => ({ ...prev, [k]: e.target.value }));
                                      }}
                                      placeholder={t('paymentDetails.actions.amountPlaceholder')}
                                    />
                                    <div className="installment-action-buttons">
                                      <button
                                        className="secondary-action-button"
                                        onClick={() => handleInstallmentUpdate(installment, 'increase')}
                                        disabled={actionLoadingKey === `increase:${getInstallmentKey(installment)}`}
                                      >
                                        {actionLoadingKey === `increase:${getInstallmentKey(installment)}`
                                          ? t('paymentDetails.actions.processing')
                                          : t('paymentDetails.actions.increase')}
                                      </button>
                                      <button
                                        className="secondary-action-button danger"
                                        onClick={() => handleInstallmentUpdate(installment, 'decrease')}
                                        disabled={actionLoadingKey === `decrease:${getInstallmentKey(installment)}`}
                                      >
                                        {actionLoadingKey === `decrease:${getInstallmentKey(installment)}`
                                          ? t('paymentDetails.actions.processing')
                                          : t('paymentDetails.actions.decrease')}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="error-message">{t('paymentDetails.noDetails')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;

